'use client';

import type { Color, FontVariant } from '@onefootprint/design-tokens';
import { cx } from 'class-variance-authority';
import type { ElementType, HTMLAttributes } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import mergeRefs from 'react-merge-refs';
import variantMapping from './text.constants';
export type TextProps = {
  truncate?: boolean;
  variant: FontVariant;
  color?: Color | string;
  tag?: ElementType;
  testID?: string;
  marginBottom?: number;
  marginTop?: number;
  marginLeft?: number;
  marginRight?: number;
  padding?: number;
  paddingTop?: number;
  paddingBottom?: number;
  paddingLeft?: number;
  paddingRight?: number;
  textAlign?: 'left' | 'center' | 'right' | 'justify' | 'inherit' | 'start' | 'end';
  whiteSpace?: string;
  width?: string | number;
  maxWidth?: string | number;
  isPrivate?: boolean;
  display?: string;
  gap?: number;
  overflow?: string;
  textOverflow?: string;
  justifyContent?: string;
  center?: boolean;
} & HTMLAttributes<HTMLParagraphElement>;
const Text = forwardRef<HTMLParagraphElement, TextProps>(({
  tag = 'p',
  children,
  truncate,
  variant,
  color = 'primary',
  title,
  className,
  testID,
  marginBottom,
  marginTop,
  marginLeft,
  marginRight,
  padding,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  textAlign,
  whiteSpace,
  width,
  isPrivate,
  display,
  gap,
  overflow,
  textOverflow,
  justifyContent,
  center,
  maxWidth,
  style,
  ...props
}: TextProps, ref) => {
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);
  const textTitle = title || (typeof children === 'string' ? children : undefined);
  useEffect(() => {
    if (!truncate) {
      return;
    }
    const element = textRef.current;
    if (element) {
      setIsEllipsisActive(element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight);
    } else {
      setIsEllipsisActive(false);
    }
  }, [truncate]);
  const Tag = (tag || variantMapping[variant] || 'p') as ElementType;
  const spacingMap = (value?: number) => {
    if (value === undefined) return '';
    const map: Record<number, string> = {
      1: '0.5',
      2: '1',
      3: '2',
      4: '3',
      5: '4',
      6: '5',
      7: '6',
      8: '8',
      9: '10',
      10: '16'
    };
    return map[value] || '';
  };
  const gapMap = (value_0?: number) => {
    if (value_0 === undefined) return '';
    const map_0: Record<number, string> = {
      1: '0.5',
      2: '1',
      3: '2',
      4: '3',
      5: '4',
      6: '5',
      7: '6',
      8: '8',
      9: '10',
      10: '16'
    };
    return map_0[value_0] || '';
  };
  return <Tag {...props} ref={mergeRefs([textRef, ref])} data-testid={testID} data-dd-privacy={isPrivate ? 'mask' : undefined} className={cx(`text-${variant}`, color && `text-${color}`, {
    'overflow-hidden text-ellipsis whitespace-nowrap': truncate,
    [`mb-${spacingMap(marginBottom)}`]: marginBottom !== undefined,
    [`mt-${spacingMap(marginTop)}`]: marginTop !== undefined,
    [`ml-${spacingMap(marginLeft)}`]: marginLeft !== undefined,
    [`mr-${spacingMap(marginRight)}`]: marginRight !== undefined,
    [`p-${spacingMap(padding)}`]: padding !== undefined,
    [`pt-${spacingMap(paddingTop)}`]: paddingTop !== undefined,
    [`pb-${spacingMap(paddingBottom)}`]: paddingBottom !== undefined,
    [`pl-${spacingMap(paddingLeft)}`]: paddingLeft !== undefined,
    [`pr-${spacingMap(paddingRight)}`]: paddingRight !== undefined,
    [`text-${textAlign}`]: textAlign !== undefined,
    [`whitespace-${whiteSpace}`]: whiteSpace !== undefined,
    [`w-${width}`]: typeof width === 'string' && width,
    [`${display}`]: display !== undefined,
    [`gap-${gapMap(gap)}`]: gap !== undefined,
    [`overflow-${overflow}`]: overflow !== undefined,
    [`text-${textOverflow}`]: textOverflow !== undefined,
    [`justify-${justifyContent}`]: justifyContent !== undefined,
    'flex items-center justify-center': center
  }, 'fp-text', className)} style={{
    width: typeof width === 'number' ? `${width}px` : width,
    maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
    ...style
  }} title={isEllipsisActive ? textTitle : undefined}>
        {children}
      </Tag>;
});
export default Text;